import { useForm } from "@tanstack/react-form";
import { useState } from "react";
import { useTranslations } from "use-intl";
import { z } from "zod";

import { slugify } from "@lawline/shared/text";
import { Button } from "@lawline/ui/components/button";
import { Field, FieldError, FieldLabel } from "@lawline/ui/components/field";
import { Input } from "@lawline/ui/components/input";

import { authClient } from "@platform/auth/client";
import {
	sessionMutationOptions,
	useSessionTransition,
} from "@platform/auth/session-transition.context";

export function CreateTenant() {
	const t = useTranslations("tenant.create");
	const [error, setError] = useState<string | null>(null);
	const changeSession = useSessionTransition();
	const schema = z.object({ name: z.string().trim().min(1, t("nameRequired")) });
	const form = useForm({
		defaultValues: { name: "" },
		validators: { onSubmit: schema },
		onSubmit: async ({ value }) => {
			setError(null);
			await changeSession(async () => {
				const name = value.name.trim();
				const { data, error: createError } = await authClient.organization.create({
					name,
					slug: slugify(name),
					fetchOptions: sessionMutationOptions,
				});
				if (
					createError &&
					createError.status >= 400 &&
					createError.status < 500 &&
					createError.status !== 408
				) {
					setError(createError.message ?? t("error"));
					return "rejected";
				}
				if (createError || !data?.id) throw new Error("Organization creation is unconfirmed");
				return "reload";
			});
		},
	});

	return (
		<div className="flex min-h-[60vh] items-center justify-center">
			<form
				onSubmit={(event) => {
					event.preventDefault();
					void form.handleSubmit();
				}}
				className="flex w-full max-w-sm flex-col gap-3"
			>
				<h1 className="text-center text-xl font-semibold tracking-tight">{t("title")}</h1>
				<form.Subscribe selector={(state) => state.isSubmitting}>
					{(pending) => (
						<>
							<form.Field name="name">
								{(field) => {
									const invalid = field.state.meta.isTouched && !field.state.meta.isValid;
									return (
										<Field data-invalid={invalid}>
											<FieldLabel htmlFor="tenant-name">{t("placeholder")}</FieldLabel>
											<Input
												id="tenant-name"
												name={field.name}
												autoFocus
												autoComplete="organization"
												value={field.state.value}
												onBlur={field.handleBlur}
												onChange={(event) => field.handleChange(event.target.value)}
												disabled={pending}
												aria-invalid={invalid}
												aria-describedby={invalid ? "tenant-name-error" : undefined}
											/>
											{invalid && (
												<FieldError id="tenant-name-error" errors={field.state.meta.errors} />
											)}
										</Field>
									);
								}}
							</form.Field>
							<Button type="submit" disabled={pending}>
								{pending ? t("creating") : t("submit")}
							</Button>
						</>
					)}
				</form.Subscribe>
				{error && (
					<p role="alert" className="text-destructive text-center text-sm">
						{error}
					</p>
				)}
			</form>
		</div>
	);
}
